import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';
interface ContrastCheckerModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (backgroundColor: string) => void;
  initialBackgroundColor?: string;
}

export const ContrastCheckerModal: React.FC<ContrastCheckerModalProps> = ({
  open,
  onClose,
  onSave,
  initialBackgroundColor = '#FFFFFF',
}) => {
  const theme = useTheme();
  const [backgroundColor, setBackgroundColor] = useState(initialBackgroundColor);

  const handleColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = event.target.value.toUpperCase();
    setBackgroundColor(newColor);
  };

  const handleHexInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value.toUpperCase();
    
    // Ensure it starts with #
    if (value && !value.startsWith('#')) {
      value = '#' + value;
    }
    
    // Allow hex color format (up to 6 hex digits after #)
    if (/^#[0-9A-F]{0,6}$/.test(value)) {
      setBackgroundColor(value);
    } else if (value === '') {
      setBackgroundColor('#');
    }
  };

  // Validate if hex color is complete and valid
  const isValidHexColor = (color: string): boolean => {
    return /^#[0-9A-F]{6}$/.test(color);
  };

  const isSaveDisabled = !isValidHexColor(backgroundColor);

  const handleSave = () => {
    onSave(backgroundColor);
    onClose();
  };

  const handleClose = () => {
    setBackgroundColor('#FFFFFF');
    onClose();
  };

  // Update when modal opens or initialBackgroundColor changes
  useEffect(() => {
    if (open) {
      setBackgroundColor(initialBackgroundColor);
    }
  }, [open, initialBackgroundColor]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          bgcolor: theme.palette.background.paper,
        },
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1,
        borderBottom: '1px solid',
        borderColor: theme.palette.divider,
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          Contrast Checker
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': {
              bgcolor: theme.palette.action.hover,
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Typography 
          variant="body2" 
          sx={{ 
            color: theme.palette.text.secondary,
            mb: 3,
          }}
        >
          Select a background to calculate contrast ratios for the entire palette.
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography 
            variant="subtitle2" 
            sx={{ 
              fontWeight: 600,
              color: theme.palette.text.primary,
              mb: 2,
            }}
          >
            Background for Contrast
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              component="label"
              sx={{
                width: 48,
                height: 48,
                borderRadius: 1,
                bgcolor: backgroundColor,
                border: '1px solid',
                borderColor: theme.palette.divider,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                '&:hover': {
                  borderColor: theme.palette.mode === 'dark' ? '#008080' : theme.palette.primary.main,
                  borderWidth: '2px',
                },
              }}
            >
              <input
                type="color"
                value={backgroundColor}
                onChange={handleColorChange}
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  cursor: 'pointer',
                }}
              />
            </Box>
            
            <TextField
              value={backgroundColor}
              onChange={handleHexInputChange}
              placeholder="#FFFFFF"
              size="small"
              error={!isValidHexColor(backgroundColor) && backgroundColor.length > 0}
              sx={{
                width: 120,
                '& .MuiOutlinedInput-root': {
                  bgcolor: theme.palette.mode === 'dark' 
                    ? theme.palette.background.paper 
                    : theme.palette.grey[50],
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.divider,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.mode === 'dark' ? '#008080' : theme.palette.primary.main,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.mode === 'dark' ? '#008080' : theme.palette.primary.main,
                  },
                },
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        px: 3, 
        pb: 3,
        borderTop: '1px solid',
        borderColor: theme.palette.divider,
      }}>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSaveDisabled}
          sx={{
            textTransform: 'none',
            bgcolor: theme.palette.mode === 'dark' ? '#008080' : theme.palette.primary.main,
            '&:hover': {
              bgcolor: theme.palette.mode === 'dark' ? '#008080' : theme.palette.primary.main,
            },
            '&.Mui-disabled': {
              bgcolor: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled,
            },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

